/* global firebase */
import { useState, useEffect } from 'react';
const db = firebase.firestore();



/**
 * A react hook to load all users.. 
 */
export function useUsers() {
  const [users, setUsers] = useState(null);
  
  useEffect(() => {
    db
      .collection('users')
      .get()
      .then(snap => {
        const all = [];
        snap.forEach(doc => 
          all.push({
            id: doc.id,
            ...doc.data()
          })
        );
        setUsers(all);
      });
  }, []);
  
  return users;
}



/**
 * A hook that watches a single user by id.
 * 
 * @param {String} userId
 */
export function useUser(userId) {
  const [user, setUser] = useState(null);
  
  useEffect(() => {
    if(userId) {
      return db
        .collection('users')
        .doc(userId)
        .onSnapshot(doc => {
          const data = doc.data() || {};
          setUser({
            id: userId,
            ...data,
          });
        });
    }
    else {
      setUser({});
    }
  }, [userId]);
  
  return user;
}


/**
 * A hook that loads user by company.
 * 
 * @param {String} companyId
 */
export function useCompanyUsers(companyId) {
  const [users, setUsers] = useState(null);
  
  useEffect(() => {
    
    if(!companyId) {
      setUsers([]);
      return;
    }
    
    return db
      .collection('companies')
      .doc(companyId)
      .collection('users')
      .onSnapshot(snap => {
        const users = [];
        for(const doc of snap.docs) {
          users.push({
            id: doc.id, 
            ...doc.data(),
          });
        }
        setUsers(users);
      });
    
  }, [companyId]);
  
  return users;
}


/**
 * Create a new user.
 * 
 * @param {Object} user
 * @param {String} user.displayName
 * @param {String} user.email
 */
export async function createUser(userId, user={}) {
  
  // todo: check permissions
  // note this is used by auth when registering new users
  
  await db
    .collection('users')
    .doc(userId)
    .set(user);
    
  return {
    ...user,
    id: userId,
  };
}


/**
 * Update the logged in user's company info. 
 * 
 * @param {String} userId
 * 
 * @param {Object} user
 * @param {String} user.name
 * @param {String} user.street
 * @param {String} user.city
 * @param {String} user.state
 * @param {String} user.zip
 */
export async function updateUser(userId, update={}) {
  
  if(update.role) {
    const user = await db
      .collection('users')
      .doc(userId)
      .get()
      .then(doc => doc.data());
    
    await db
      .collection('companies')
      .doc(user.companyId)
      .collection('users')
      .doc(userId)
      .update({role:update.role});
  }
  
  return await db
    .collection('users')
    .doc(userId)
    .update(update);
}


/**
 * Invite a user to be a contributor on this company.
 * 
 * @param {String} companyId
 * @param {String} email
 */
export async function inviteUser(email, companyId) {
  
  await db
    .collection("invites")
    .add({
      companyId, 
      email,
      dateSent: Date.now(),
    });
  
}


/**
 * A hook to get an invitation by id.
 * 
 * @param {String} inviteId
 * 
 */
export function useInvite(inviteId) {
  const [invite, setInvite] = useState(null);
  
  useEffect(() => {
    if(!inviteId) {
      setInvite(null);
      return;
    }
    db
      .collection('invites')
      .doc(inviteId)
      .get()
      .then(doc => {
        if(!doc.exists) {
          setInvite(false);
        }
        setInvite(doc.data());
      });
    
  }, [inviteId]);
  
  return invite;
}

/**
 * A hook to subscribe to all company invitations.
 * 
 * @param {String} companyId
 * 
 */
export function useInvites(companyId) {
  const [invites, setInvites] = useState(null);
  
  useEffect(() => {
    if(!companyId) {
      setInvites([]);
      return;
    }
    db
      .collection('invites')
      .where("companyId", "==", companyId)
      .get()
      .then(snap => {
        const invites = [];
        snap.forEach(doc => invites.push({
          id: doc.id,
          ...doc.data()
        }));
        setInvites(invites);
      });
    
  }, [companyId]);
  
  return invites;
}


/**
 * Remove invitation.
 * 
 * @param {String} inviteId
 * 
 */
export function removeInvite(inviteId) {
  return db
    .collection('invites')
    .doc(inviteId)
    .delete();
}